import { Configuration, ProductCategoriesApi } from '@/api';
import { fetchAllPaginated } from '@/api/utils';
import SelectBox, {
  Item,
  MultiSelectBoxProps,
  SingleSelectBoxProps,
} from '@/components/select/SelectBox';
import { useAuth } from '@/modules/auth/contexts/AuthContext';

type ProductCategorySelectBoxProps<T extends Item> =
  | (SingleSelectBoxProps<T> & {
      supplierId?: string | string[] | null;
      mapper?: (item: T) => T;
    })
  | (MultiSelectBoxProps<T> & {
      supplierId?: string | string[];
      mapper?: (item: T) => T;
    });

function toArray<T>(value: T | T[] | undefined | null): T[] {
  if (Array.isArray(value)) return value;
  if (value !== undefined && value !== null) return [value];
  return [];
}

const ProductCategorySelectBox = <T extends Item>(props: ProductCategorySelectBoxProps<T>) => {
  const productCategoriesApi = new ProductCategoriesApi(new Configuration(useAuth));

  const supplierIds = toArray(props.supplierId);

  const deduplicate = <T extends Item>(items: T[]): T[] => {
    const map = new Map<string | number, T>();
    for (const item of items) {
      map.set(item.name, item);
    }
    return Array.from(map.values());
  };

  const listProductCategories = async (): Promise<T[]> => {
    const productCategories = await fetchAllPaginated(
      (offset, limit) =>
        supplierIds.length > 0
          ? productCategoriesApi.findProductCategories(undefined, { supplierIds }, offset, limit)
          : productCategoriesApi.listProductCategories(offset, limit),
      100,
      1000,
    );

    const items = productCategories as unknown as T[];
    const mappedItems = props.mapper ? items.map(props.mapper) : items;

    return deduplicate(mappedItems);
  };

  const { label = 'Category:', placeholder = 'Select category' } = props;

  return (
    <SelectBox<T>
      {...props}
      name="productCategory"
      label={label}
      placeholder={placeholder}
      queryFn={listProductCategories}
      queryKey={['productCategory', ...supplierIds]}
    />
  );
};

export default ProductCategorySelectBox;
